/**
 * JSON-LD schema.org structured data for marquettabryant.com.
 *
 * All schemas are ready-to-inject objects. Use via <PageSEO jsonLd={[...]}/>.
 * Validate changes at https://validator.schema.org and
 * https://search.google.com/test/rich-results
 */

const SITE = "https://marquettabryant.com";
// Must be stable public URLs. Vite fingerprints /assets/* filenames, so
// bundled images cannot be referenced here; use files from /public.
const HEADSHOT = `${SITE}/og-image.jpg`;
const LOGO = `${SITE}/apple-touch-icon.png`;
const NMLS_URL =
  "https://www.nmlsconsumeraccess.org/EntityDetails.aspx/INDIVIDUAL/2512081";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Schema = Record<string, any>;

/* ---------- Person (homepage) ---------- */
export const personSchema: Schema = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": `${SITE}/#person`,
  name: "Marquetta Bryant",
  url: SITE,
  image: HEADSHOT,
  jobTitle: [
    "Licensed Real Estate Broker",
    "Mortgage Advisor",
    "Founder & CEO",
  ],
  description:
    "Marquetta Bryant is a licensed real estate broker and Mortgage Advisor (NMLS #2512081) based in Metro Atlanta, GA. Founder of Fresh Vision Realty, Fresh Vision Academy, and The Mabel & Augustus Institute for Family Freedom. Specializes in AI-powered real estate systems, complex mortgage scenarios, and generational wealth strategy for entrepreneurs.",
  knowsAbout: [
    "Real Estate Brokerage",
    "Mortgage Lending",
    "AI Automation for Real Estate",
    "Generational Wealth Strategy",
    "Entrepreneurship",
    "SaaS Product Design",
    "Founder Psychology",
    "1031 Exchange Strategy",
    "DSCR Loans",
    "Digital Twin Technology",
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Atlanta",
    addressRegion: "GA",
    addressCountry: "US",
  },
  sameAs: [
    "https://www.threads.com/@marquetta_fvg",
    "https://www.linkedin.com/in/marquetta-bryant-9548a817a/",
    "https://www.instagram.com/marquetta_fvg/",
    "https://youtube.com/@marquettabryant",
    "https://www.tiktok.com/@marquetta_bryant",
    "https://marquettas-newsletter.beehiiv.com/",
    "https://webuildfamily.com/",
    "https://freshvisionrealty.com/",
  ],
  hasCredential: {
    "@type": "EducationalOccupationalCredential",
    credentialCategory: "NMLS License",
    recognizedBy: {
      "@type": "Organization",
      name: "Nationwide Multistate Licensing System",
      url: "https://www.nmlsconsumeraccess.org/",
    },
    identifier: "NMLS #2512081",
  },
  founder: [
    {
      "@type": "Organization",
      name: "Fresh Vision Realty",
      url: "https://freshvisionrealty.com/",
    },
    {
      "@type": "Organization",
      name: "Fresh Vision Academy",
      url: `${SITE}/academy`,
    },
    {
      "@type": "Organization",
      name: "The Mabel & Augustus Institute for Family Freedom",
      url: "https://webuildfamily.com/",
    },
  ],
  email: "marquetta@freshvisionrealty.com",
};

/* ---------- Organization (homepage) ---------- */
export const organizationSchema: Schema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE}/#organization`,
  name: "Marquetta Bryant",
  url: SITE,
  logo: {
    "@type": "ImageObject",
    url: LOGO,
    width: 800,
    height: 800,
  },
  image: HEADSHOT,
  email: "marquetta@freshvisionrealty.com",
  founder: { "@id": `${SITE}/#person` },
  address: {
    "@type": "PostalAddress",
    addressLocality: "Atlanta",
    addressRegion: "GA",
    addressCountry: "US",
  },
  sameAs: [
    "https://www.threads.com/@marquetta_fvg",
    "https://www.linkedin.com/in/marquetta-bryant-9548a817a/",
    "https://www.instagram.com/marquetta_fvg/",
    "https://youtube.com/@marquettabryant",
    "https://www.tiktok.com/@marquetta_bryant",
    "https://freshvisionrealty.com/",
    "https://freshvisionacademy.org/",
  ],
};

/* ---------- ProfilePage (/about) ---------- */
export const profilePageSchema: Schema = {
  "@context": "https://schema.org",
  "@type": "ProfilePage",
  "@id": `${SITE}/about#profilepage`,
  url: `${SITE}/about`,
  mainEntity: { "@id": `${SITE}/#person` },
};

/* ---------- RealEstateAgent + LocalBusiness (/real-estate) ---------- */
export const realEstateAgentSchema: Schema = {
  "@context": "https://schema.org",
  "@type": ["RealEstateAgent", "LocalBusiness", "ProfessionalService"],
  "@id": `${SITE}/real-estate#business`,
  name: "Fresh Vision Realty — Marquetta Bryant",
  url: `${SITE}/real-estate`,
  image: HEADSHOT,
  logo: LOGO,
  founder: { "@id": `${SITE}/#person` },
  description:
    "Fresh Vision Realty is a Metro Atlanta real estate brokerage founded by Marquetta Bryant, providing strategic real estate services for home buyers, sellers, and investors across Cobb, Fulton, DeKalb, Henry, Douglas counties and Buford. Specializing in AI-powered real estate operations, capital structuring, and generational wealth architecture.",
  email: "marquetta@freshvisionrealty.com",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Atlanta",
    addressRegion: "GA",
    addressCountry: "US",
  },
  areaServed: [
    { "@type": "City", name: "Atlanta" },
    { "@type": "AdministrativeArea", name: "Cobb County, Georgia" },
    { "@type": "AdministrativeArea", name: "Fulton County, Georgia" },
    { "@type": "AdministrativeArea", name: "DeKalb County, Georgia" },
    { "@type": "AdministrativeArea", name: "Henry County, Georgia" },
    { "@type": "AdministrativeArea", name: "Douglas County, Georgia" },
    { "@type": "City", name: "Buford, Georgia" },
  ],
  serviceType: [
    "Residential Real Estate Sales",
    "Investment Property Acquisition",
    "Capital Structuring",
    "Asset Deployment Strategy",
    "Cash-Flow Engineering",
    "Exit Strategy & Succession Planning",
    "1031 Exchange Positioning",
  ],
  employee: { "@id": `${SITE}/#person` },
  sameAs: ["https://freshvisionrealty.com/"],
};

/* ---------- FinancialService + LocalBusiness (/mortgage) with NMLS ---------- */
export const financialServiceSchema: Schema = {
  "@context": "https://schema.org",
  "@type": ["FinancialService", "LocalBusiness"],
  "@id": `${SITE}/mortgage#business`,
  name: "Marquetta Bryant, Mortgage Advisor, One Real Mortgage",
  url: `${SITE}/mortgage`,
  description:
    "Mortgage Advisor Marquetta Bryant (NMLS #2512081) with One Real Mortgage. Specializing in conventional, FHA, VA, DSCR, and investment loans for entrepreneurs, 1099 earners, and first-time buyers in Metro Atlanta and nationally.",
  image: HEADSHOT,
  logo: LOGO,
  email: "marquetta@freshvisionrealty.com",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Atlanta",
    addressRegion: "GA",
    addressCountry: "US",
  },
  identifier: {
    "@type": "PropertyValue",
    name: "NMLS",
    value: "2512081",
    url: NMLS_URL,
  },
  serviceType: [
    "Conventional Mortgage Loans",
    "FHA Loans",
    "VA Loans",
    "Investment Property Loans",
    "DSCR Loans",
    "Self-Employed Mortgage Solutions",
    "Credit Repair Guidance",
    "Mortgage Pre-Approval",
  ],
  areaServed: { "@type": "Country", name: "United States" },
  employee: { "@id": `${SITE}/#person` },
  parentOrganization: {
    "@type": "Organization",
    name: "One Real Mortgage",
  },
};

/* ---------- FAQPage for /mortgage ---------- */
export const mortgageFaqSchema: Schema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Can I get a mortgage if I'm self-employed or have 1099 income?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Marquetta Bryant specializes in mortgage solutions for 1099 earners and entrepreneurs. We reposition your income documentation to demonstrate stability and maximize your debt-to-income ratio, even if traditional retail banks have turned you down. NMLS #2512081.",
      },
    },
    {
      "@type": "Question",
      name: "What is a DSCR loan and who qualifies?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A Debt Service Coverage Ratio (DSCR) loan is an investment property loan qualified based on the property's rental income rather than your personal income. It's ideal for real estate investors who have strong rental yield but complex personal tax returns. We offer DSCR loans through One Real Mortgage (NMLS #2512081).",
      },
    },
    {
      "@type": "Question",
      name: "What loan types does Marquetta Bryant offer?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We offer conventional loans, FHA loans (low down payment for first-time buyers), VA loans (for veterans and military), investment property loans, DSCR loans, and credit repair guidance. All loans are originated through One Real Mortgage (NMLS #2512081).",
      },
    },
    {
      "@type": "Question",
      name: "How do I get pre-approved for a mortgage?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Start your pre-approval by clicking 'Apply Now' on our mortgage page or scheduling a Capital Review call. Marquetta will audit your income documentation, credit profile, and entity structure to position you for the strongest possible approval before you shop for a property.",
      },
    },
    {
      "@type": "Question",
      name: "What areas does Marquetta Bryant serve for mortgage loans?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "As a licensed mortgage loan officer (NMLS #2512081) with One Real Mortgage, Marquetta is licensed to originate loans in multiple states. Primary markets include Metro Atlanta (Cobb, Fulton, DeKalb, Henry, and Douglas counties). Contact us to confirm lending availability in your state.",
      },
    },
  ],
};

/* ---------- FAQPage for /real-estate ---------- */
export const realEstateFaqSchema: Schema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What makes Fresh Vision Realty different from other Atlanta real estate brokerages?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Fresh Vision Realty is an AI-powered, multi-state brokerage led by Marquetta Bryant — a licensed real estate broker and Mortgage Advisor. We don't just close deals; we architect generational freedom. Our MARQUETTA Method combines capital structuring, asset deployment, cash-flow engineering, and exit strategy into a unified wealth architecture plan.",
      },
    },
    {
      "@type": "Question",
      name: "Which Metro Atlanta counties does Fresh Vision Realty serve?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We serve buyers, sellers, and investors throughout Metro Atlanta including Cobb, Fulton, DeKalb, Henry, Douglas counties and Buford. As a multi-state operation, we also have capabilities beyond Georgia — contact us to discuss your specific market.",
      },
    },
    {
      "@type": "Question",
      name: "Can Fresh Vision Realty help real estate investors?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. We specialize in investment property acquisition, multi-family strategy, DSCR loan structuring, cash-flow engineering, and 1031 exchange positioning. Because Marquetta is both a broker and a licensed mortgage loan officer (NMLS #2512081), she sees the full board from initial acquisition through long-term portfolio succession.",
      },
    },
    {
      "@type": "Question",
      name: "What is the MARQUETTA Method?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The MARQUETTA Method is a four-step proprietary process: (1) Capital Structuring — repositioning your income so underwriters approve your file; (2) Asset Deployment — strategic property acquisition based on math, not hope; (3) Cash-Flow Engineering — ensuring every asset is self-sustaining; (4) Exit Strategy & Succession — building systems so your wealth outlives you.",
      },
    },
  ],
};

/* ---------- FAQPage for /tech-founder-summit ---------- */
export const summitFaqSchema: Schema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Is there a replay?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Replays are available for VIP ticket holders only. VIP members get lifetime access to all recordings.",
      },
    },
    {
      "@type": "Question",
      name: "Do I need to know how to code?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "No coding experience required. We use no-code and low-code tools that anyone can learn.",
      },
    },
    {
      "@type": "Question",
      name: "Is this for Loan Officers too?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. This summit is designed for both Real Estate Agents and Loan Officers who want to own their tech stack.",
      },
    },
  ],
};

/* ---------- Article: /blog/real-estate-ai-automation-2026 ---------- */
export const aiAutomation2026ArticleSchema: Schema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline:
    "The 2026 Real Estate Agent's Guide to AI Automation: What to Build, What to Buy, What to Skip",
  description:
    "A practitioner's breakdown of real estate AI automation in 2026, the Build/Buy/Skip framework, tool recommendations, and how to build an AI-powered practice that scales, from Marquetta Bryant of Fresh Vision Realty.",
  image: `${SITE}/og-image.jpg`,
  author: {
    "@type": "Person",
    name: "Marquetta Bryant",
    url: `${SITE}/about`,
    jobTitle: "Broker, Mortgage Loan Originator, Systems Strategist",
    identifier: {
      "@type": "PropertyValue",
      name: "NMLS",
      value: "2512081",
    },
    worksFor: [
      { "@type": "Organization", name: "Fresh Vision Realty", url: SITE },
      {
        "@type": "Organization",
        name: "One Real Mortgage Corp.",
        url: "https://onerealmortgage.com",
      },
    ],
  },
  publisher: {
    "@type": "Organization",
    name: "Marquetta Bryant | Fresh Vision Realty",
    url: SITE,
    logo: {
      "@type": "ImageObject",
      url: `${SITE}/og-image.jpg`,
    },
  },
  datePublished: "2026-04-23",
  dateModified: "2026-04-23",
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${SITE}/blog/real-estate-ai-automation-2026`,
  },
  keywords: [
    "real estate AI automation 2026",
    "AI digital twin real estate agent",
    "real estate agent systems",
    "AI tools real estate 2026",
    "Fresh Vision Academy",
  ],
  articleSection: "Real Estate Technology",
  inLanguage: "en-US",
};

/* ---------- FAQPage: /blog/real-estate-ai-automation-2026 ---------- */
export const aiAutomation2026FaqSchema: Schema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is the most important AI tool for real estate agents in 2026?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The most impactful AI investment for real estate agents in 2026 is not a single tool, it is a connected content operating system. This means a documented brand voice and content strategy paired with AI tools (ChatGPT for writing, Descript or CapCut for video, and a smart CRM for lead management) that work together to produce consistent, high-quality content and follow-up without requiring the agent to be present for every step.",
      },
    },
    {
      "@type": "Question",
      name: "What is an AI digital twin for real estate agents?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "An AI digital twin is an AI model trained on your specific voice, knowledge, content archive, and workflows. It can respond to client inquiries, draft content in your style, and execute follow-up sequences that sound like you, not a generic chatbot. Building an effective digital twin requires a documented brand foundation before you begin training.",
      },
    },
    {
      "@type": "Question",
      name: "How do real estate agents use AI without losing the personal touch?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The most effective agents use AI to automate the operational and administrative layers, scheduling, follow-up, content distribution, market report generation, so that all available human attention is reserved for the relationship moments that AI cannot replicate: negotiation, difficult conversations, client trust-building. The goal is not to replace presence; it is to protect presence by eliminating the tasks that consume it unnecessarily.",
      },
    },
    {
      "@type": "Question",
      name: "Can AI help real estate agents close more deals?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes, when applied systematically. AI-powered CRMs with predictive lead scoring analyze behavioral signals and property data to help agents identify the highest-priority contacts in their database. Automated follow-up sequences ensure no warm lead goes cold due to operational oversight. Agents using these tools report significant improvements in lead-to-appointment conversion because the follow-up is consistent regardless of how busy the agent is.",
      },
    },
    {
      "@type": "Question",
      name: "What should real estate agents avoid when adopting AI?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The three most common mistakes are: (1) adopting multiple disconnected tools without an integration strategy, (2) automating outreach before documenting a clear brand voice, which produces generic content at scale, and (3) treating AI adoption as a one-time setup rather than an ongoing operational discipline. AI tools evolve rapidly; agents who build a system for continuous learning and adaptation outperform those who set and forget.",
      },
    },
  ],
};

/* ---------- BreadcrumbList factory ---------- */
export const breadcrumb = (
  items: { name: string; path: string }[]
): Schema => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: `${SITE}${item.path}`,
  })),
});

/* ---------- Article: /blog/automate-real-estate-lead-qualification ---------- */
export const automateLeadQualificationArticleSchema: Schema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline:
    "How to Automate Real Estate Lead Qualification With AI",
  description:
    "A practical step-by-step guide to automating real estate lead qualification with AI. Build scoring models, smart intake workflows, and handoff systems that surface only high-intent prospects.",
  image: `${SITE}/og-image.jpg`,
  author: {
    "@type": "Person",
    name: "Marquetta Bryant",
    url: `${SITE}/about`,
    jobTitle: "Broker, Mortgage Loan Originator, Systems Strategist",
    identifier: {
      "@type": "PropertyValue",
      name: "NMLS",
      value: "2512081",
    },
    worksFor: [
      { "@type": "Organization", name: "Fresh Vision Realty", url: SITE },
      {
        "@type": "Organization",
        name: "One Real Mortgage Corp.",
        url: "https://onerealmortgage.com",
      },
    ],
  },
  publisher: {
    "@type": "Organization",
    name: "Marquetta Bryant | Fresh Vision Realty",
    url: SITE,
    logo: {
      "@type": "ImageObject",
      url: `${SITE}/og-image.jpg`,
    },
  },
  datePublished: "2026-06-13",
  dateModified: "2026-06-13",
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${SITE}/blog/automate-real-estate-lead-qualification`,
  },
  keywords: [
    "automate real estate lead qualification",
    "AI lead qualification real estate",
    "real estate lead scoring",
    "automated lead qualification workflow",
    "real estate CRM automation",
    "Fresh Vision Realty",
  ],
  articleSection: "Real Estate Technology",
  inLanguage: "en-US",
};

/* ---------- FAQPage: /blog/automate-real-estate-lead-qualification ---------- */
export const automateLeadQualificationFaqSchema: Schema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is AI lead qualification in real estate?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "AI lead qualification is the use of automated systems, chatbots, forms, and CRM scoring to collect, evaluate, and prioritize potential buyer and seller leads before a human agent ever speaks with them. It filters out cold inquiries, surfaces high-intent prospects, and ensures agents only spend time on conversations that are mathematically likely to close.",
      },
    },
    {
      "@type": "Question",
      name: "Can AI really qualify leads better than a human agent?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "AI is not better than a human at relationship building, but it is infinitely better than a human at consistency, speed, and scale. AI can respond to every inquiry in under 60 seconds, 24 hours a day, ask the same qualifying questions every single time, and score leads against objective criteria without fatigue or bias. The best systems use AI for the first 80 percent of qualification and a human agent for the final 20 percent, the conversation that converts trust into a signed agreement.",
      },
    },
    {
      "@type": "Question",
      name: "What tools do I need to automate real estate lead qualification?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "At minimum, you need three layers: (1) a capture mechanism, a smart intake form or AI chatbot that collects structured data; (2) a CRM with automation rules and lead scoring, such as Follow Up Boss, kvCORE, or HubSpot; and (3) a communication automation layer, SMS and email sequences triggered by lead behavior and score thresholds. Many agents also add an AI conversational layer, like ChatGPT-powered chatbots or tools like Structurely, to handle natural-language inquiries before routing.",
      },
    },
    {
      "@type": "Question",
      name: "How do I stop high-quality leads from slipping through the cracks?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The most common failure point is setting qualification criteria too narrowly. Build a scoring model with multiple dimensions, timeline, motivation, financing readiness, property type fit, and communication responsiveness, rather than relying on a single yes/no gate. Review your disqualified lead cohort monthly. If leads are being rejected for solvable problems, like needing credit guidance, route them to a nurture track instead of deleting them.",
      },
    },
    {
      "@type": "Question",
      name: "How long does it take to set up an automated lead qualification system?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A basic automated qualification workflow, smart form + CRM scoring + follow-up sequences, can be built in a single focused day if you already have a CRM. A fully integrated system with an AI chatbot, predictive scoring, and dynamic nurture tracks typically takes one to two weeks to configure, test, and refine. The return on that time investment is immediate: agents reclaim 10 to 20 hours per week previously spent on unqualified conversations.",
      },
    },
  ],
};
